/**
 * Cambodian phone numbers. Customers type them in every conceivable shape
 * ("012 345 678", "+855 12 345 678", "85512345678"), and we key COD follow-up
 * and customer records on them, so normalise to E.164 once, here.
 */

const KH_COUNTRY_CODE = '855';

/** Mobile prefixes in use in Cambodia, without the leading 0. */
const KH_MOBILE_PREFIXES = [
  '10', '11', '12', '13', '14', '15', '16', '17', '18', '31', '38', '39',
  '60', '61', '66', '67', '68', '69', '70', '71', '76', '77', '78', '79',
  '81', '85', '86', '87', '88', '89', '90', '92', '93', '95', '96', '97', '98', '99',
];

export interface NormalizedPhone {
  /** E.164, e.g. "+85512345678". */
  e164: string;
  /** Local display form, e.g. "012 345 678". */
  local: string;
}

export function normalizePhone(input: string): NormalizedPhone | null {
  const digits = input.replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (digits === '') return null;

  let national: string;
  if (digits.startsWith(KH_COUNTRY_CODE)) {
    national = digits.slice(KH_COUNTRY_CODE.length);
  } else if (digits.startsWith('0')) {
    national = digits.slice(1);
  } else {
    national = digits;
  }
  national = national.replace(/^0+/, '');

  // Cambodian subscriber numbers are 8 or 9 digits after the leading zero.
  if (national.length < 8 || national.length > 9) return null;

  const prefix2 = national.slice(0, 2);
  const prefix3 = national.slice(0, 3);
  const known = KH_MOBILE_PREFIXES.includes(prefix2) || KH_MOBILE_PREFIXES.includes(prefix3);
  if (!known) return null;

  const rest = national.slice(2);
  const grouped =
    rest.length === 6
      ? `${rest.slice(0, 3)} ${rest.slice(3)}`
      : `${rest.slice(0, 3)} ${rest.slice(3, 6)}${rest.length > 6 ? ` ${rest.slice(6)}` : ''}`;

  return {
    e164: `+${KH_COUNTRY_CODE}${national}`,
    local: `0${prefix2} ${grouped}`.replace(/\s+/g, ' ').trim(),
  };
}

export function isValidKhmerPhone(input: string): boolean {
  return normalizePhone(input) !== null;
}
